/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
    Button,
    DialogActions,
    Typography,
    makeStyles,
} from '@material-ui/core';
import {
    bool, func, object, string,
} from 'prop-types';
import CloseIcon from '@material-ui/icons/Close';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import InputLabel from '@material-ui/core/InputLabel';
import TextField from '@material-ui/core/TextField';
import { useDispatch } from 'react-redux';
import RadioButton from '../../Subscriptions/Partials/RadioButton/RadioButton';
import EventDispatcher from '../../../state/reducers/EventDispatcher';

const useStyles = makeStyles((theme) => ({
    paperProps: {
        [theme.breakpoints.up('sm')]: {
            minWidth: '55%',
            height: '91%',
            maxHeight: '600px',
        },
        maxHeight: 'calc(100% - 64px)',
        maxWidth: '670px',
        boxShadow: '0px 0px 20px 6px rgba(0,0,0,0.5)',
        borderRadius: '0',
        margin: '0 19.5px',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
    },
    dialogTitle: {
        display: 'flex',
        borderBottom: '1px solid black',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '15px',
        fontWeight: 600,
        padding: 0,
        '& span': {
            padding: '12px',
        },
    },
    closeIcon: {
        padding: 0,
    },
    dialogContent: {
        padding: '6px',
        width: '100%',
        overflowY: 'visible',
    },
    panelContainer: {
        width: '100%',
        display: 'flex',
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
        },
    },
    panel: {
        [theme.breakpoints?.up('sm')]: {
            padding: '15px',
        },
        flex: '1',
        overflow: 'auto',
        [theme.breakpoints?.up('md')]: {
            '&:first-of-type': {
                borderRight: '1px solid #dddddd',
            },
        },
    },
    radioContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    radioLabel: {
        color: 'black',
        fontSize: '14px',
        fontWeight: 600,
        fontFamily: theme.typography.fontFamily,
    },
    recipientMessage: {
        fontSize: '13px',
        padding: '6px 14px',
        margin: 0,
    },
    fieldset: {
        border: 'none',
    },
    inputTitle: {
        fontWeight: 700,
        fontSize: 'unset',
        color: 'black',
        '&:last-of-type': {
            marginTop: '20px',
        },
    },
    giftMessageInput: {
        width: '100%',
        marginTop: '16px',
    },
    formTextInput: {
        [theme.breakpoints?.down('sm')]: {
            fontSize: '1rem',
        },
    },
    giftMaxMessage: {
        display: 'block',
        marginTop: '5px',
    },
    noRecipientMessage: {
        fontSize: '1rem',
        fontWeight: 400,
        [theme.breakpoints?.up('sm')]: {
            fontWeight: 600,
            fontSize: '13px',
        },
        padding: '6px 14px',
        margin: 0,
    },
    secondaryButtonStyle: {
        backgroundColor: 'transparent',
        color: theme.palette.black,
        fontSize: '14px',
        fontWeight: 'bold',
        padding: '8px 16px',
        borderRadius: '30px',
        border: `1px solid ${theme.palette.black}`,
        boxShadow: '2px 2px 4px rgba(47, 47, 47, 0.15)',
        width: 'calc(100% - 10px)',
        flexBasis: '25%',
        [theme.breakpoints.down('sm')]: {
            flexBasis: '50%',
        },
        '&:hover': {
            color: theme.palette.white,
            backgroundColor: theme.palette.colorPrimary,
        },
    },
    primaryButtonStyle: {
        color: theme.palette.white,
        backgroundColor: theme.palette.primaryButton,
        fontSize: '14px',
        fontWeight: 'bold',
        padding: '8px 16px',
        borderRadius: '30px',
        border: `1px solid ${theme.palette.primaryButton}`,
        boxShadow: '2px 2px 4px rgba(47, 47, 47, 0.15)',
        width: 'calc(100% - 10px)',
        flexBasis: '25%',
        [theme.breakpoints.down('sm')]: {
            flexBasis: '50%',
        },
        '&:hover': {
            backgroundColor: theme.palette.cms?.primary_hover,
        },
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'center',
        margin: '32px 0 0 0',
        gap: '16px',
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            padding: '0 14px',
            margin: '10px 0',
        },
    },
}));

// NUMBER_OF_GIFT_MESSAGE_LINES is the sum of lines without signature line
const responseGiftConfig = {
    1012: {
        brandCode: 'CCO',
        NUMBER_OF_GIFT_MESSAGE_LINES: 4,
        NUMBER_OF_CHARACTERS_PER_LINE: 30,
        NUMBER_OF_CHARACTERS_OF_SIGNATURE: 24,
    },
    1002: {
        brandCode: 'TPF',
        NUMBER_OF_GIFT_MESSAGE_LINES: 4,
        NUMBER_OF_CHARACTERS_PER_LINE: 30,
        NUMBER_OF_CHARACTERS_OF_SIGNATURE: 24,
    },
    1019: {
        brandCode: 'HD',
        NUMBER_OF_GIFT_MESSAGE_LINES: 3,
        NUMBER_OF_CHARACTERS_PER_LINE: 29,
        NUMBER_OF_CHARACTERS_OF_SIGNATURE: 24,
    },
    1009: {
        brandCode: '18B',
        NUMBER_OF_GIFT_MESSAGE_LINES: 5,
        NUMBER_OF_CHARACTERS_PER_LINE: 35,
        NUMBER_OF_CHARACTERS_OF_SIGNATURE: 29,
    },
    1018: {
        brandCode: 'SY',
        NUMBER_OF_GIFT_MESSAGE_LINES: 3,
        NUMBER_OF_CHARACTERS_PER_LINE: 29,
        NUMBER_OF_CHARACTERS_OF_SIGNATURE: 18,
    },
    1020: {
        brandCode: 'WLF',
        NUMBER_OF_GIFT_MESSAGE_LINES: 3,
        NUMBER_OF_CHARACTERS_PER_LINE: 29,
        NUMBER_OF_CHARACTERS_OF_SIGNATURE: 18,
    },
    1025: {
        brandCode: 'SCH',
        NUMBER_OF_GIFT_MESSAGE_LINES: 6,
        NUMBER_OF_CHARACTERS_PER_LINE: 24,
        NUMBER_OF_CHARACTERS_OF_SIGNATURE: 17,
    },
    1031: {
        brandCode: 'VC',
        NUMBER_OF_GIFT_MESSAGE_LINES: 4,
        NUMBER_OF_CHARACTERS_PER_LINE: 29,
        NUMBER_OF_CHARACTERS_OF_SIGNATURE: 18,
    },
    1001: {
        brandCode: '18F',
        NUMBER_OF_GIFT_MESSAGE_LINES: 1,
        NUMBER_OF_CHARACTERS_PER_LINE: 180,
        NUMBER_OF_CHARACTERS_OF_SIGNATURE: 14,
    },
    1029: {
        brandCode: 'BRY',
        NUMBER_OF_GIFT_MESSAGE_LINES: 1,
        NUMBER_OF_CHARACTERS_PER_LINE: 180,
        NUMBER_OF_CHARACTERS_OF_SIGNATURE: 14,
    },
    1010: {
        brandCode: 'FB',
        NUMBER_OF_GIFT_MESSAGE_LINES: 1,
        NUMBER_OF_CHARACTERS_PER_LINE: 180,
        NUMBER_OF_CHARACTERS_OF_SIGNATURE: 14,
    },
    1034: {  // Using flowers setup for now.  update information when available
        brandCode: 'FL',
        NUMBER_OF_GIFT_MESSAGE_LINES: 1,
        NUMBER_OF_CHARACTERS_PER_LINE: 180,
        NUMBER_OF_CHARACTERS_OF_SIGNATURE: 14,
    },
};

const SubscriptionEditGiftMessage = ({
    isOpen, handleClose, selectedSubscription, giftMessage, signatureMessage,
}) => {
    const isGPTProduct = ['1001', '1010', '1029'].includes(selectedSubscription?.brand) && selectedSubscription?.recipient?.[0]?.productType === 'GPT';
    const giftConfig = responseGiftConfig[isGPTProduct ? 1019 : selectedSubscription?.brand || 1019 /* Default to HD */];

    const classes = useStyles();
    const [giftMessageEnabled, setGiftMessageEnabled] = useState(true);
    const [inputValues, setInputValues] = useState(Array(giftConfig.NUMBER_OF_GIFT_MESSAGE_LINES).fill(''));
    const [signature, setSignature] = useState('');
    const inputRefs = useRef([]);
    const dispatch = useDispatch();

    const giftMessageCharacterLimit = giftConfig.NUMBER_OF_GIFT_MESSAGE_LINES * giftConfig.NUMBER_OF_CHARACTERS_PER_LINE + giftConfig.NUMBER_OF_CHARACTERS_OF_SIGNATURE;
    const isSingleLine = giftConfig.NUMBER_OF_GIFT_MESSAGE_LINES === 1;

    const splitTextIntoLines = (message) => {
        const words = message.match(/\S+/g) || [];
        const lines = Array(giftConfig.NUMBER_OF_GIFT_MESSAGE_LINES).fill('');
        let currentLineIndex = 0;
        let currentLineMessage = '';

        words.forEach((word) => {
            const spaceChar = currentLineMessage.length ? ' ' : '';
            if (currentLineMessage.length + word.length + spaceChar.length <= giftConfig.NUMBER_OF_CHARACTERS_PER_LINE) {
                currentLineMessage = `${currentLineMessage}${spaceChar}${word}`;
            } else {
                lines[currentLineIndex] = currentLineMessage;
                currentLineMessage = word;
                currentLineIndex += 1;
            }
        });

        if (currentLineMessage.length) {
            lines[currentLineIndex] = currentLineMessage;
            currentLineIndex += 1;
        }

        return lines;
    };

    const handleGiftMessageChange = (e, index) => {
        const value = e.target.value;
        const separateSpaces = value?.split(' ');
        const lastWord = separateSpaces?.pop() || '';
        const nextLine = inputValues[index + 1];

        if (value.length > giftConfig.NUMBER_OF_CHARACTERS_PER_LINE) return null;

        if ((value.slice(-1) === ' ' || lastWord.length === value.length)
            && index < inputValues.length - 1
            && value.length >= giftConfig.NUMBER_OF_CHARACTERS_PER_LINE) {
            inputRefs.current[index + 1]?.focus();
        }

        if (value.length === giftConfig.NUMBER_OF_CHARACTERS_PER_LINE
            && lastWord
            && lastWord.length < giftConfig.NUMBER_OF_CHARACTERS_PER_LINE
            && index < inputValues.length - 1
            && !nextLine.length) {
            const newInputValues = [...inputValues];

            newInputValues[index] = separateSpaces.join(' ');
            newInputValues[index + 1] = lastWord;

            inputRefs.current[index + 1]?.focus();
            return setInputValues(newInputValues);
        }
        return setInputValues(inputValues.map((curr, ind) => (ind === index ? value : curr)));
    };

    const handleGiftMessageInputDown = (e, index) => {
        if (e.key === 'Backspace' && e.target.value === '' && index) {
            e.preventDefault();
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleKeyFilter = (e) => {
        const restrictedCharacters = /[$#%^*=|{}[\]\\;<>~`]/;
        if (restrictedCharacters.test(e?.key)) e.preventDefault();
    };

    const handleGiftMessageSubmit = (e) => {
        e.preventDefault();
        const combinedInputVals = inputValues.join(' ').replace(/\s+/g, ' ').trim();
        const cleanSignature = signature.replace(/\s+/g, ' ').trim();
        const updatedGiftMessage = `${combinedInputVals} ${cleanSignature ? `FROM: ${cleanSignature}` : ''}`;
        const putData = {
            ruleId: selectedSubscription?.ruleId,
            giftMessage: giftMessageEnabled ? updatedGiftMessage.trim() : '',
            closeForm: handleClose,
            getAuthor: !isSingleLine && giftMessageEnabled,
        };
        dispatch(EventDispatcher.updateSubscriptionGiftMessage(putData));
    };

    useEffect(() => {
        if (isOpen) {
            // Set initial gift message from response on open
            const lines = splitTextIntoLines(giftMessage);
            if (lines.length === giftConfig.NUMBER_OF_GIFT_MESSAGE_LINES) setInputValues(lines);
            setSignature(signatureMessage.length <= giftConfig.NUMBER_OF_CHARACTERS_OF_SIGNATURE ? signatureMessage : '');
            setGiftMessageEnabled(!!giftMessage || !!signatureMessage);
        }
    }, [giftMessage, signatureMessage, isOpen]);

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            classes={{
                paper: classes.paperProps,
            }}
        >
            <form className={classes.form} onSubmit={(e) => handleGiftMessageSubmit(e)}>
                <DialogTitle disableTypography className={classes.dialogTitle}>
                    <span>RECIPIENT GIFT MESSAGE</span>
                    <IconButton onClick={handleClose} className={classes.closeIcon}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <div className={classes.panelContainer}>
                        <div className={classes.panel}>
                            <div className={classes.radioContainer}>
                                <RadioButton
                                    id="gift-message-radio-enabled"
                                    checked={giftMessageEnabled}
                                    onChange={() => setGiftMessageEnabled(!giftMessageEnabled)}
                                    value="yes"
                                    name="radio-yes"
                                />
                                <InputLabel
                                    htmlFor="gift-message-radio-enabled"
                                    className={classes.radioLabel}
                                >
                                    Complimentary Gift Message
                                </InputLabel>
                            </div>
                            {giftMessageEnabled && (
                                <>
                                    <Typography className={classes.recipientMessage}><i>Include your name so the recipient knows who sent the gift</i></Typography>
                                    <fieldset className={classes.fieldset}>
                                        {!isSingleLine && <legend className={classes.inputTitle}>Your Message</legend>}
                                        {isSingleLine ? (
                                            <TextField
                                                autoComplete="off"
                                                value={inputValues[0]}
                                                onKeyPress={handleKeyFilter}
                                                onChange={(e) => setInputValues([e?.target?.value])}
                                                inputProps={{ maxLength: giftConfig.NUMBER_OF_CHARACTERS_PER_LINE }}
                                                className={classes.giftMessageInput}
                                                label="Write your gift message here..."
                                                multiline
                                                minRows={7}
                                                required
                                                InputLabelProps={{
                                                    classes: {
                                                        root: classes.formTextInput,
                                                    },
                                                }}
                                            />
                                        ) : inputValues.map((value, index) => (
                                            <TextField
                                                autoComplete="off"
                                                value={value}
                                                onKeyPress={handleKeyFilter}
                                                onChange={(e) => handleGiftMessageChange(e, index)}
                                                onKeyDown={(e) => handleGiftMessageInputDown(e, index)}
                                                inputRef={(element) => inputRefs.current.splice(index, 1, element)}
                                                inputProps={{ maxLength: giftConfig.NUMBER_OF_CHARACTERS_PER_LINE }}
                                                className={classes.giftMessageInput}
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={index}
                                                required={index === 0 && inputValues?.reduce((accumulator, currentValue) => accumulator + currentValue.length, 0) <= 0}
                                                InputLabelProps={{
                                                    classes: {
                                                        root: classes.formTextInput,
                                                    },
                                                }}
                                            />
                                        ))}
                                        {!isSingleLine && <InputLabel className={classes.inputTitle} htmlFor="signature-field">From</InputLabel>}
                                        <TextField
                                            id="signature-field"
                                            onKeyPress={handleKeyFilter}
                                            onChange={(e) => setSignature(e.target.value)}
                                            value={signature}
                                            inputProps={{ maxLength: giftConfig.NUMBER_OF_CHARACTERS_OF_SIGNATURE }}
                                            autoComplete="off"
                                            className={classes.giftMessageInput}
                                            label={isSingleLine && 'From'}
                                            required={isSingleLine}
                                        />
                                        <Typography variant="caption" className={classes.giftMaxMessage}>
                                            {giftMessageCharacterLimit - (inputValues?.reduce((accumulator, currentValue) => accumulator + currentValue.length, 0) + signature.length)} characters left
                                        </Typography>
                                    </fieldset>
                                </>
                            )}
                        </div>
                        <div className={classes.panel}>
                            <div className={classes.radioContainer}>
                                <RadioButton
                                    id="gift-message-radio-disabled"
                                    checked={!giftMessageEnabled}
                                    onChange={() => setGiftMessageEnabled(!giftMessageEnabled)}
                                    value="no"
                                    name="radio-no"
                                />
                                <InputLabel
                                    htmlFor="gift-message-radio-disabled"
                                    className={classes.radioLabel}
                                >
                                    No Gift Message
                                </InputLabel>
                            </div>
                            <Typography variant="body2" className={classes.noRecipientMessage}>If you do not include a gift message, the recipient won&apos;t know who sent this gift.</Typography>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <div className={classes.buttonContainer}>
                        <Button onClick={handleClose} fullWidth className={classes.secondaryButtonStyle}>Cancel</Button>
                        <Button fullWidth type="submit" className={classes.primaryButtonStyle}>Save</Button>
                    </div>
                </DialogActions>
            </form>
        </Dialog>
    );
};

SubscriptionEditGiftMessage.propTypes = {
    isOpen: bool.isRequired,
    handleClose: func.isRequired,
    selectedSubscription: object.isRequired,
    giftMessage: string.isRequired,
    signatureMessage: string.isRequired,
};

export default SubscriptionEditGiftMessage;
